import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { CURRENCY_LABEL_FORMATS, getCurrencyOptions, isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';
import EditableCard from 'modules/common/editableCard.component';
import React, { useState } from 'react';
import { postGlobalAssignment, updateGlobalAssignment, getGlobalInfo } from 'modules/authorizations/store/actions/global.actions';
import { POST_GLOBAL_ASSIGNMENT_FAILURE, UPDATE_GLOBAL_ASSIGNMENT_FAILURE } from 'modules/authorizations/store/types/globalTypes';
import { GET_GLOBAL_INFO_FAILURE } from 'modules/authorizations/types';

const FinancialDetailsGlobalCard = props => {
  const [isLoading, setIsLoading] = useState(false);

  const { globalInfo, id, isReadOnly, enumerations } = props;

  const basicOptions = ['Yes', 'No'];

  const currencyOptions = getCurrencyOptions(enumerations?.currencies, CURRENCY_LABEL_FORMATS.LONG);
  const selectedCurrency = currencyOptions.find(currency =>
    isIdEqual(currency.id, globalInfo?.payInCurrencyId) || isIdEqual(currency.code, globalInfo?.payInCurrencyId)
  );
  const defaultCurrency = currencyOptions.find(currency => currency.code === 'USD');
  const currencyValue = selectedCurrency?.id || defaultCurrency?.id || '';

  // TODO: Add max number of trips as enum in the DB once business input is received on which options are allowed.
  const maxNumTrips = [
    { title: '', id: '00000000-0000-0000-0000-000000000000' },
    { title: 'One Trip', id: '8f72b0b5-5722-471f-ae69-0ad9fe685ca8' },
    { title: 'Two Trips', id: '71f4770a-b489-4e6a-af1c-ca86370cad10' },
    { title: 'Three Trips', id: '6553b8d0-0c3c-4463-8fa3-02d8bcca3488' }
  ];

  const tabs = [{ header: 'Financial Details', order: 1, iconPath: ['fas', 'dollar-sign'] }];

  const summaries = [
    {
      header: 'Pay In Currency',
      accessorKey: 'payInCurrencyId',
      type: 'dropdown-v2',
      required: false,
      value: currencyValue,
      options: currencyOptions,
      tab: 1
    },
    {
      header: 'Eligible For Tax Service',
      accessorKey: 'isEligibleForTaxService',
      type: 'dropdown',
      required: false,
      value: globalInfo && globalInfo?.isEligibleForTaxService === true ? basicOptions[0] : basicOptions[1],
      options: basicOptions,
      tab: 1
    },
    {
      header: 'Max # of Trips',
      accessorKey: 'maxNumberOfTrips',
      type: 'dropdown-v2',
      required: false,
      value: globalInfo?.maxNumberOfTrips,
      options: maxNumTrips,
      tab: 1
    },
    {
      header: 'Trip Cap Amt',
      accessorKey: 'tripCapAmount',
      type: 'number',
      required: false,
      value: globalInfo?.tripCapAmount,
      tab: 1
    },
    {
      header: 'Cert of Coverage Exp',
      accessorKey: 'certOfCoverageExpires',
      type: 'date',
      required: false,
      value: globalInfo ? globalInfo.certOfCoverageExpires : '',
      tab: 1
    }
  ];

  const updateFields = async (tab, values) => {
    setIsLoading(true);
    if (globalInfo) {
      let newValues = { ...globalInfo };

      newValues.payInCurrencyId = values.payInCurrencyId;
      newValues.maxNumberOfTrips = values.maxNumberOfTrips;
      newValues.tripCapAmount = values.tripCapAmount;
      newValues.certOfCoverageExpires =
        values && values.certOfCoverageExpires && values.certOfCoverageExpires.length > 0 ? values.certOfCoverageExpires : null;

      if (values.isEligibleForTaxService === 'Yes' || values.isEligibleForTaxService === true) {
        newValues.isEligibleForTaxService = true;
      } else {
        newValues.isEligibleForTaxService = false;
      }

      const resp = await props.updateGlobalAssignment(newValues);
      if (resp.type === UPDATE_GLOBAL_ASSIGNMENT_FAILURE) {
        props.showToast('Failed to update global service financial info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setIsLoading(false);
        return false;
      } else {
        const getResp = await props.getGlobalInfo(id);
        if (getResp.type === GET_GLOBAL_INFO_FAILURE) {
          props.showToast('Failed to get global service info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          setIsLoading(false);
          return false;
        } else {
          setIsLoading(false);
          return true;
        }
      }
    } else {
      let newValues = {
        authorizationFileId: id,
        assignmentTypeId: '00000000-0000-0000-0000-000000000000',
        assignmentCountryId: '00000000-0000-0000-0000-000000000000',
        assignmentCity: '',
        assignmentLengthId: '00000000-0000-0000-0000-000000000000',
        clientAssignmentNumber: '',
        homeCountryId: '00000000-0000-0000-0000-000000000000',
        homeStateId: '00000000-0000-0000-0000-000000000000',
        citizenshipId: '00000000-0000-0000-0000-000000000000',
        payInCurrencyId: values.payInCurrencyId,
        isEligibleForTaxService: values.isEligibleForTaxService,
        notes: '',
        tripCapAmount: values.tripCapAmount,
        maxNumberOfTrips: values.maxNumberOfTrips,
        estimatedStart: null,
        estimatedEnd: null,
        actualStart: null,
        actualEnd: null,
        hireDate: null,
        completedDate: null,
        cancelledDate: null,
        certOfCoverageExpires: values.certOfCoverageExpires,
        isReturnToHome: false
      };

      const resp = await props.postGlobalAssignment(newValues);
      if (resp.type === POST_GLOBAL_ASSIGNMENT_FAILURE) {
        props.showToast('Failed to update global service financial info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        setIsLoading(false);
        return false;
      } else {
        const getResp = await props.getGlobalInfo(id);
        if (getResp.type === GET_GLOBAL_INFO_FAILURE) {
          props.showToast('Failed to get global service info, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          setIsLoading(false);
          return false;
        } else {
          setIsLoading(false);
          return true;
        }
      }
    }
  };

  return (
    <EditableCard
      tabs={tabs}
      summaries={summaries}
      numColumns={3}
      updateFields={updateFields}
      isLoading={isLoading}
      hideEdit={isReadOnly}
    />
  );
};

export default compose(
  connect(null, {
    updateGlobalAssignment,
    postGlobalAssignment,
    getGlobalInfo,
    showToast
  })
)(FinancialDetailsGlobalCard);
